import React, { useState, useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  Divider,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  Switch,
} from '@mui/material';
import {
  Download,
  Sun,
  Moon,
  Camera,
  Palette,
  RotateCw,
  Settings,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  FileText,
  Globe,
  CreditCard,
  Upload,
  AlertCircle,
  User,
  RefreshCcw
} from 'lucide-react';

// Import the DB service
import {
  saveSettings,
  loadSettings,
  clearSettings,
  saveToCookie,
  loadFromCookie,
  clearCookie,
  isIndexedDBAvailable
} from './db-service';

// Color schemes
const colorSchemes = {
  blue: {
    name: 'Corporate Blue',
    frontBg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#3b82f6',
    secondary: '#1e293b',
    accent: '#60a5fa',
    cardBorder: 'none'
  },
  white: {
    name: 'Minimal White',
    frontBg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    frontText: '#1e293b',
    frontHighlight: '#e2e8f0',
    frontAccent: '#94a3b8',
    backBg: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    backText: '#0f172a',
    backHighlight: '#f1f5f9',
    backAccent: '#cbd5e1',
    primary: '#0f172a',
    secondary: '#475569',
    accent: '#94a3b8',
    cardBorder: '1px solid #e2e8f0'
  },
  teal: {
    name: 'Modern Teal',
    frontBg: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: 'linear-gradient(135deg, #134e4a 0%, #0f766e 100%)',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#14b8a6',
    secondary: '#0f766e',
    accent: '#5eead4',
    cardBorder: 'none'
  },
  purple: {
    name: 'Creative Purple',
    frontBg: 'linear-gradient(135deg, #6b21a8 0%, #9333ea 100%)',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: 'linear-gradient(135deg, #581c87 0%, #7e22ce 100%)',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#9333ea',
    secondary: '#581c87',
    accent: '#c084fc',
    cardBorder: 'none'
  },
  dark: {
    name: 'Elegant Dark',
    frontBg: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
    frontText: '#f9fafb',
    frontHighlight: 'rgba(255,255,255,0.1)',
    frontAccent: 'rgba(255,255,255,0.6)',
    backBg: 'linear-gradient(135deg, #030712 0%, #111827 100%)',
    backText: '#f9fafb',
    backHighlight: 'rgba(255,255,255,0.1)',
    backAccent: 'rgba(255,255,255,0.6)',
    primary: '#e5e7eb',
    secondary: '#9ca3af',
    accent: '#f9fafb',
    cardBorder: '1px solid #374151'
  },
  red: {
    name: 'Dynamic Red',
    frontBg: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#ef4444',
    secondary: '#b91c1c',
    accent: '#fca5a5',
    cardBorder: 'none'
  },
  green: {
    name: 'Natural Green',
    frontBg: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: 'linear-gradient(135deg, #14532d 0%, #15803d 100%)',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#22c55e',
    secondary: '#166534',
    accent: '#86efac',
    cardBorder: 'none'
  },
  amber: {
    name: 'Warm Amber',
    frontBg: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
    frontText: 'white',
    frontHighlight: 'rgba(255,255,255,0.2)',
    frontAccent: 'rgba(255,255,255,0.8)',
    backBg: 'linear-gradient(135deg, #92400e 0%, #d97706 100%)',
    backText: 'white',
    backHighlight: 'rgba(255,255,255,0.2)',
    backAccent: 'rgba(255,255,255,0.8)',
    primary: '#f59e0b',
    secondary: '#b45309',
    accent: '#fcd34d',
    cardBorder: 'none'
  }
};

// Design patterns
const designPatterns = {
  none: {
    name: "None",
    pattern: "none",
    opacity: 0
  },
  dots: {
    name: "Dots",
    pattern: "radial-gradient(#ffffff33 1px, transparent 1px)",
    backgroundSize: "20px 20px",
    opacity: 0.15
  },
  lines: {
    name: "Lines",
    pattern: "repeating-linear-gradient(45deg, #ffffff26 0, #ffffff26 1px, transparent 1px, transparent 10px)",
    opacity: 0.12
  },
  grid: {
    name: "Grid",
    pattern: "linear-gradient(#ffffff33 1px, transparent 1px), linear-gradient(90deg, #ffffff33 1px, transparent 1px)",
    backgroundSize: "20px 20px",
    opacity: 0.1
  },
  zigzag: {
    name: "Zig Zag",
    pattern: "linear-gradient(135deg, #ffffff18 25%, transparent 25%) -10px 0, linear-gradient(225deg, #ffffff18 25%, transparent 25%) -10px 0, linear-gradient(315deg, #ffffff18 25%, transparent 25%), linear-gradient(45deg, #ffffff18 25%, transparent 25%)",
    backgroundSize: "20px 20px",
    opacity: 0.15
  },
  waves: {
    name: "Waves",
    pattern: "repeating-radial-gradient(#ffffff33 2px, transparent 4px, #ffffff33 6px)",
    backgroundSize: "60px 60px",
    opacity: 0.1
  },
  diamonds: {
    name: "Diamonds",
    pattern: "linear-gradient(45deg, #ffffff18 25%, transparent 25%) 0 0, linear-gradient(-45deg, #ffffff18 25%, transparent 25%) 0 0, linear-gradient(45deg, transparent 75%, #ffffff18 75%) 0 0, linear-gradient(-45deg, transparent 75%, #ffffff18 75%) 0 0",
    backgroundSize: "20px 20px",
    opacity: 0.12
  },
  hexagons: {
    name: "Hexagons",
    pattern: "radial-gradient(circle at center, #ffffff33 1%, transparent 6%) 0 0, radial-gradient(circle at center, #ffffff33 1%, transparent 6%) 24px 24px",
    backgroundSize: "48px 48px",
    opacity: 0.15
  }
};

// Card layouts
const cardLayouts = {
  standard: {
    name: 'Standard',
    avatarSize: { xs: 120, sm: 140, md: 160 },
    showHeader: true,
    infoLayout: 'centered',
    includeFooter: true
  },
  modern: {
    name: 'Modern',
    avatarSize: { xs: 110, sm: 130, md: 150 },
    showHeader: true,
    infoLayout: 'left-aligned',
    includeFooter: false
  },
  minimalist: {
    name: 'Minimalist',
    avatarSize: { xs: 100, sm: 120, md: 140 },
    showHeader: false,
    infoLayout: 'centered',
    includeFooter: false
  },
  compact: {
    name: 'Compact',
    avatarSize: { xs: 90, sm: 110, md: 130 },
    showHeader: true,
    infoLayout: 'compact',
    includeFooter: true
  },
  executive: {
    name: 'Executive',
    avatarSize: { xs: 130, sm: 150, md: 170 },
    showHeader: true,
    infoLayout: 'elegant',
    includeFooter: true
  },
  corporate: {
    name: 'Corporate',
    avatarSize: { xs: 100, sm: 120, md: 140 },
    showHeader: true,
    infoLayout: 'structured',
    includeFooter: true
  }
};

// Badge styles
const badgeStyles = {
  standard: {
    name: 'Standard',
    borderRadius: 1,
    padding: '0.5rem 1rem'
  },
  rounded: {
    name: 'Rounded',
    borderRadius: 4,
    padding: '0.5rem 1rem'
  },
  pill: {
    name: 'Pill',
    borderRadius: 50,
    padding: '0.5rem 1.2rem'
  },
  square: {
    name: 'Square',
    borderRadius: 0,
    padding: '0.5rem 1rem'
  },
  ribbon: {
    name: 'Ribbon',
    borderRadius: '0 12px 12px 0',
    padding: '0.5rem 1.2rem',
    transform: 'translateX(-8px)'
  },
  tag: {
    name: 'Tag',
    borderRadius: '0 4px 4px 0',
    padding: '0.4rem 1rem',
    borderLeft: '4px solid currentColor'
  }
};

const ProfilePageIDCard = ({ profileData }) => {
  // Refs for card elements
  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);
  const containerRef = useRef(null);
  const qrCodeRef = useRef(null);
  const logoInputRef = useRef(null);
  const initialLoadComplete = useRef(false);
const [issueDate, setIssueDate] = useState(profileData.joinDate || "");
const [validUntil, setValidUntil] = useState(profileData.expiryDate || "");
  // State for card customization
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [colorScheme, setColorScheme] = useState('blue');
  const [designPattern, setDesignPattern] = useState('none');
  const [cardLayout, setCardLayout] = useState('standard');
  const [badgeStyle, setBadgeStyle] = useState('rounded');
  const [activeSettingsTab, setActiveSettingsTab] = useState(0);
  const [logoPreview, setLogoPreview] = useState(null);
  const [useLogoAsBg, setUseLogoAsBg] = useState(false);
  const [useStorage, setUseStorage] = useState(true);
  const [idCardNumber, setIdCardNumber] = useState(profileData.employeeId || "");
  const handleIdCardNumberChange = (event) => {
    setIdCardNumber(event.target.value);
  };
  // Emergency contact info
  const [emergencyContact, setEmergencyContact] = useState({
    name: "",
    relation: "",
    phone: "",
    email: ""
  });

  // Customization options
  const [customOptions, setCustomOptions] = useState({
    includePhoto: true,
    includeID: true,
    includePosition: true,
    includeDepartment: true,
    includeCompany: true,
    includeIssueDates: true,
    includeQR: true,
    includeContactInfo: true,
    includeSkills: false, // Default to false as requested
    includeEmergencyInfo: true,
    includeLogo: false,
    qrCodeType: 'vcard', // 'vcard' or 'link'
    showBorder: true,
    roundedCorners: true,
    showShadow: true,
    customText: ''
  });
  // Load saved settings on component mount
  useEffect(() => {
    const loadSavedSettings = async () => {
      try {
        setLoading(true);

        let savedSettings;

        // Try to load from IndexedDB first, fall back to cookies if needed
        if (isIndexedDBAvailable()) {
          savedSettings = await loadSettings();
        } else {
          savedSettings = loadFromCookie();
        }

        // Only update state if we actually found saved settings
        if (savedSettings) {
          // Extract all the settings from the saved object
          if (savedSettings.colorScheme) setColorScheme(savedSettings.colorScheme);
          if (savedSettings.designPattern) setDesignPattern(savedSettings.designPattern);
          if (savedSettings.cardLayout) setCardLayout(savedSettings.cardLayout);
          if (savedSettings.badgeStyle) setBadgeStyle(savedSettings.badgeStyle);
          if (savedSettings.customOptions) setCustomOptions(savedSettings.customOptions);
          if (savedSettings.emergencyContact) setEmergencyContact(savedSettings.emergencyContact);
          if (savedSettings.useLogoAsBg !== undefined) setUseLogoAsBg(savedSettings.useLogoAsBg);
          if (savedSettings.logoPreview) setLogoPreview(savedSettings.logoPreview);
          if (savedSettings.idCardNumber) setIdCardNumber(savedSettings.idCardNumber);
          setSnackbar({
            open: true,
            message: 'Your saved settings have been loaded',
            severity: 'info'
          });
        }
      } catch (error) {
        console.error('Error loading saved settings:', error);
      } finally {
        setLoading(false);
        initialLoadComplete.current = true;
      }
    };

    loadSavedSettings();
  }, []);
  // Save settings whenever they change
  useEffect(() => {
    // Skip the first render to avoid saving default settings
    if (!initialLoadComplete.current || !useStorage) return;

    const saveUserSettings = async () => {
      // Collect all settings in one object
      const settingsToSave = {
        colorScheme,
        designPattern,
        cardLayout,
        badgeStyle,
        customOptions,
        emergencyContact,
        useLogoAsBg,
        logoPreview,
        idCardNumber,
        lastSaved: new Date().toISOString()
      };

      // Try IndexedDB first, fall back to cookies
      if (isIndexedDBAvailable()) {
        await saveSettings(settingsToSave);
      } else {
        saveToCookie(settingsToSave);
      }
    };

    // Use a debounce to avoid saving on every keystroke
    const timeoutId = setTimeout(() => {
      saveUserSettings();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    colorScheme,
    designPattern,
    cardLayout,
    badgeStyle,
    customOptions,
    emergencyContact,
    useLogoAsBg,
    logoPreview,
    useStorage
  ]);

  // Reset all settings to defaults
  const handleResetSettings = async () => {
    // Default settings
    const defaultSettings = {
      colorScheme: 'blue',
      designPattern: 'none',
      cardLayout: 'standard',
      badgeStyle: 'rounded',
      customOptions: {
        includePhoto: true,
        includeID: true,
        includePosition: true,
        includeDepartment: true,
        includeCompany: true,
        includeIssueDates: true,
        includeQR: true,
        includeContactInfo: true,
        includeSkills: false,
        includeEmergencyInfo: true,
        includeLogo: false,
        qrCodeType: 'vcard',
        showBorder: true,
        roundedCorners: true,
        showShadow: true,
        customText: ''
      },
      emergencyContact: {
        name: "",
        relation: "",
        phone: "",
        email: ""
      },
      
      useLogoAsBg: false,
      logoPreview: null
    };
    setIdCardNumber("");
    // Update state with defaults
    setColorScheme(defaultSettings.colorScheme);
    setDesignPattern(defaultSettings.designPattern);
    setCardLayout(defaultSettings.cardLayout);
    setBadgeStyle(defaultSettings.badgeStyle);
    setCustomOptions(defaultSettings.customOptions);
    setEmergencyContact(defaultSettings.emergencyContact);
    setUseLogoAsBg(defaultSettings.useLogoAsBg);
    setLogoPreview(defaultSettings.logoPreview);

    // Clear settings from storage
    if (isIndexedDBAvailable()) {
      await clearSettings();
    } else {
      clearCookie();
    }

    setSnackbar({
      open: true,
      message: 'All settings have been reset to defaults',
      severity: 'success'
    });
  };

  // Toggle settings storage on/off
  const handleToggleStorage = (event) => {
    setUseStorage(event.target.checked);

    if (!event.target.checked) {
      setSnackbar({
        open: true,
        message: 'Settings storage disabled. Your preferences will not be saved.',
        severity: 'warning'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Settings storage enabled. Your preferences will be saved automatically.',
        severity: 'success'
      });
    }
  };
  // Get colors, layout, and pattern
  const colors = colorSchemes[colorScheme];
  const layout = cardLayouts[cardLayout];
  const badge = badgeStyles[badgeStyle];
  const pattern = designPatterns[designPattern];

  // Get background style with logo if enabled
  const getLogoBackgroundStyle = () => {
    if (useLogoAsBg && logoPreview) {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${logoPreview})`,
        backgroundSize: '60%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: 0.08,
        pointerEvents: 'none',
        zIndex: 1,
      };
    }
    return {};
  };

  // Get pattern background style
  const getPatternBackgroundStyle = () => {
    if (designPattern !== 'none') {
      const styles = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 0
      };

      // Different pattern types need different style approaches
      switch (designPattern) {
        case 'dots':
          return {
            ...styles,
            backgroundImage: 'radial-gradient(#ffffff33 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            opacity: 0.15
          };
        case 'lines':
          return {
            ...styles,
            backgroundImage: 'repeating-linear-gradient(45deg, #ffffff26 0, #ffffff26 1px, transparent 1px, transparent 10px)',
            opacity: 0.12
          };
        case 'grid':
          return {
            ...styles,
            backgroundImage: 'linear-gradient(#ffffff33 1px, transparent 1px), linear-gradient(90deg, #ffffff33 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            opacity: 0.1
          };
        case 'zigzag':
          return {
            ...styles,
            backgroundImage: 'linear-gradient(135deg, #ffffff18 25%, transparent 25%) -10px 0, linear-gradient(225deg, #ffffff18 25%, transparent 25%) -10px 0, linear-gradient(315deg, #ffffff18 25%, transparent 25%), linear-gradient(45deg, #ffffff18 25%, transparent 25%)',
            backgroundSize: '20px 20px',
            opacity: 0.15
          };
        case 'waves':
          return {
            ...styles,
            backgroundImage: 'repeating-radial-gradient(#ffffff33 2px, transparent 4px, #ffffff33 6px)',
            backgroundSize: '60px 60px',
            opacity: 0.1
          };
        case 'diamonds':
          return {
            ...styles,
            backgroundImage: 'linear-gradient(45deg, #ffffff18 25%, transparent 25%) 0 0, linear-gradient(-45deg, #ffffff18 25%, transparent 25%) 0 0, linear-gradient(45deg, transparent 75%, #ffffff18 75%) 0 0, linear-gradient(-45deg, transparent 75%, #ffffff18 75%) 0 0',
            backgroundSize: '20px 20px',
            opacity: 0.12
          };
        case 'hexagons':
          return {
            ...styles,
            backgroundImage: 'radial-gradient(circle at center, #ffffff33 1%, transparent 6%) 0 0, radial-gradient(circle at center, #ffffff33 1%, transparent 6%) 24px 24px',
            backgroundSize: '48px 48px',
            opacity: 0.15
          };
        default:
          return {};
      }
    }
    return {};
  };
  // Handle card flip
  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  // Handle color scheme change
  const handleColorSchemeChange = (event) => {
    setColorScheme(event.target.value);
  };

  // Handle design pattern change
  const handleDesignPatternChange = (event) => {
    setDesignPattern(event.target.value);
  };

  // Handle card layout change
  const handleCardLayoutChange = (event) => {
    setCardLayout(event.target.value);
  };

  // Handle badge style change
  const handleBadgeStyleChange = (event) => {
    setBadgeStyle(event.target.value);
  };

  // Handle settings tab change
  const handleSettingsTabChange = (event, newValue) => {
    setActiveSettingsTab(newValue);
  };

  // Handle custom option change
  const handleOptionChange = (option) => (event) => {
    setCustomOptions({
      ...customOptions,
      [option]: event.target.checked
    });
  };

  // Handle QR code type change
  const handleQRTypeChange = (event) => {
    setCustomOptions({
      ...customOptions,
      qrCodeType: event.target.value
    });
  };

  // Handle custom text change
  const handleCustomTextChange = (event) => {
    setCustomOptions({
      ...customOptions,
      customText: event.target.value
    });
  };

  // Handle emergency contact change
  const handleEmergencyContactChange = (field) => (event) => {
    setEmergencyContact({
      ...emergencyContact,
      [field]: event.target.value
    });
  };

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Invalid image format. Please use JPG, PNG, GIF or SVG.',
          severity: 'error'
        });
        return;
      }

      // Check file size (limit to 2MB)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: 'Image too large. Maximum size is 2MB.',
          severity: 'error'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setCustomOptions({
          ...customOptions,
          includeLogo: true
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate vCard for QR code
  const generateVCard = () => {
    // Parse the name to extract first/last name
    const nameParts = profileData.name.split(' ');
    const lastName = nameParts.length > 1 ? nameParts.pop() : '';
    const firstName = nameParts.join(' ');

    let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';

    // FN is the formatted name (display name)
    vcard += `FN:${profileData.name}\n`;

    // N is structured name (lastName;firstName;middleName;title;suffix)
    vcard += `N:${lastName};${firstName};;;\n`;
    vcard += `TITLE:${profileData.title}\n`;
    vcard += `ORG:${profileData.company}\n`;
    vcard += `EMAIL;type=WORK:${profileData.email}\n`;
    vcard += `TEL;type=CELL:${profileData.phone}\n`;
    vcard += `ADR;type=WORK:;;${profileData.companyAddress}\n`;
    vcard += `URL;type=WORK:${profileData.companyUrl}\n`;
    vcard += `URL;type=LINKEDIN:${profileData.linkedinUrl}\n`;
    vcard += `NOTE:${profileData.bio}\n`;
    vcard += 'END:VCARD';
    return vcard;
  };
  //   const generateVCard = () => {
  //     return `BEGIN:VCARD
  // VERSION:3.0
  // FN:${profileData.name || ''}
  // TITLE:${profileData.title || ''}
  // ORG:${profileData.company || ''}
  // EMAIL:${profileData.email || ''}
  // TEL:${profileData.phone || ''}
  // ADR:;;${profileData.companyAddress || ''}
  // URL:${profileData.companyUrl || ''}
  // URL;type=LINKEDIN:${profileData.linkedinUrl || ''}
  // NOTE:${profileData.bio || ''}
  // END:VCARD`;
  //   };

  // Get QR code data based on selected type
  const getQRData = () => {
    if (customOptions.qrCodeType === 'vcard') {
      return generateVCard();
    } else {
      // Create a link to a profile page
      return `https://${profileData.profileBaseUrl || 'company.com'}/profile/${profileData.id || 'employee'}`;
    }
  };

  // Create export version of the front card (no animations/transforms)
  const createFrontCardForExport = () => {
    // Create a new div element for the front card
    const frontCardDiv = document.createElement('div');
    frontCardDiv.style.width = `${frontCardRef.current.offsetWidth}px`;
    frontCardDiv.style.height = `${frontCardRef.current.offsetHeight}px`;
    frontCardDiv.style.background = colors.frontBg;
    frontCardDiv.style.color = colors.frontText;
    frontCardDiv.style.padding = '24px';
    frontCardDiv.style.borderRadius = customOptions.roundedCorners ? '16px' : '0';
    frontCardDiv.style.boxShadow = customOptions.showShadow ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : 'none';
    frontCardDiv.style.border = customOptions.showBorder ? colors.cardBorder : 'none';
    frontCardDiv.style.position = 'absolute';
    frontCardDiv.style.zIndex = '-1000';
    frontCardDiv.style.top = '-9999px';
    frontCardDiv.style.left = '-9999px';

    // Clone the front card content
    frontCardDiv.innerHTML = frontCardRef.current.innerHTML;

    // Append to body temporarily
    document.body.appendChild(frontCardDiv);

    return frontCardDiv;
  };

  // Create export version of the back card (no animations/transforms)
  const createBackCardForExport = () => {
    // Create a new div element for the back card
    const backCardDiv = document.createElement('div');
    backCardDiv.style.width = `${backCardRef.current.offsetWidth}px`;
    backCardDiv.style.height = `${backCardRef.current.offsetHeight}px`;
    backCardDiv.style.background = colors.backBg;
    backCardDiv.style.color = colors.backText;
    backCardDiv.style.padding = '24px';
    backCardDiv.style.borderRadius = customOptions.roundedCorners ? '16px' : '0';
    backCardDiv.style.boxShadow = customOptions.showShadow ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : 'none';
    backCardDiv.style.border = customOptions.showBorder ? colors.cardBorder : 'none';
    backCardDiv.style.position = 'absolute';
    backCardDiv.style.zIndex = '-1000';
    backCardDiv.style.top = '-9999px';
    backCardDiv.style.left = '-9999px';

    // Clone the back card content
    backCardDiv.innerHTML = backCardRef.current.innerHTML;

    // Append to body temporarily
    document.body.appendChild(backCardDiv);

    return backCardDiv;
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    try {
      setLoading(true);

      // Hide "Tap to flip" for PDF export
      const hideEls = document.querySelectorAll('.hide-in-pdf');
      const prevDisplays = [];
      hideEls.forEach(el => {
        prevDisplays.push(el.style.display);
        el.style.display = 'none';
      });

      // Create temporary clones for export
      const frontCardClone = createFrontCardForExport();
      const backCardClone = createBackCardForExport();

      // Wait for DOM updates
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture front card
      const frontCanvas = await html2canvas(frontCardClone, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });

      // Capture back card
      const backCanvas = await html2canvas(backCardClone, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });

      // Clean up temporary elements
      document.body.removeChild(frontCardClone);
      document.body.removeChild(backCardClone);

      // Restore "Tap to flip" after export
      hideEls.forEach((el, i) => {
        el.style.display = prevDisplays[i];
      });

      // Create PDF with both sides
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // A4 dimensions
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;

      // Calculate dimensions to maintain aspect ratio
      const imgWidth = pageWidth - (margin * 2);
      const imgHeight = (frontCanvas.height * imgWidth) / frontCanvas.width;

      // Add front image (first page)
      const frontImgData = frontCanvas.toDataURL('image/png');
      pdf.addImage(frontImgData, 'PNG', margin, (pageHeight - imgHeight) / 2, imgWidth, imgHeight);

      // Add back image (second page)
      pdf.addPage();
      const backImgData = backCanvas.toDataURL('image/png');
      pdf.addImage(backImgData, 'PNG', margin, (pageHeight - imgHeight) / 2, imgWidth, imgHeight);

      // Save the PDF
      pdf.save(`${profileData.name?.replace(/\s+/g, '_') || 'Employee'}_ID_Card.pdf`);

      setSnackbar({
        open: true,
        message: 'ID Card exported as PDF successfully! Both sides included.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export ID Card as PDF',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  const renderStorageSettings = () => {
    return (
      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <Settings size={16} sx={{ mr: 1 }} />
          Preferences Storage
        </Typography>

        {/* <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={useStorage}
                onChange={handleToggleStorage}
              />
            }
            label="Remember my settings"
          />
        </FormGroup> */}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isIndexedDBAvailable()
              ? 'Settings stored Locally'
              : 'Using cookie storage (limited)'}
          </Typography>

          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<RefreshCcw size={14} />}
            onClick={handleResetSettings}
          >
            Reset All Settings
          </Button>
        </Box>
      </Box>
    );
  };
  return (
    <Grid container spacing={3}>
      {/* Card Preview Column */}
      <Grid item xs={12} md={8}>
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            position: 'relative',
            width: '100%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            mb: 3
          }}
        >
          {/* ID Card Container */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: '300px', sm: '350px', md: '380px' },
              height: { xs: '480px', sm: '560px', md: '608px' },
              perspective: '1000px',
              cursor: 'pointer',
              marginBottom: 4,
              // This ensures the container maintains its position
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Card Inner Container (handles the flip) */}
            <Box
              onClick={handleFlip}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                // Set the transform origin to center
                transformOrigin: 'center center',
              }}
            >
              {/* Card Front */}
              <Box
                ref={frontCardRef}
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  borderRadius: customOptions.roundedCorners ? '16px' : '0',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  color: colors.frontText,
                  padding: 3,
                  boxShadow: customOptions.showShadow ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : 'none',
                  border: customOptions.showBorder ? colors.cardBorder : 'none',
                  // Ensure front card stays in position
                  transform: 'rotateY(0deg)',
                  background: colors.frontBg,
                  top: 0,
                  left: 0,
                }}
              >
                {/* Pattern Background Layer */}
                {designPattern !== 'none' && (
                  <Box sx={getPatternBackgroundStyle()} />
                )}

                {/* Logo Background Layer */}
                {useLogoAsBg && logoPreview && (
                  <Box sx={getLogoBackgroundStyle()} />
                )}

                {/* Content - now wrapped in a container with relative position and z-index */}
                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Company Logo & Header */}
                  {layout.showHeader && (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2
                    }}>
                      {customOptions.includeCompany && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {customOptions.includeLogo && logoPreview && (
                            <Box
                              component="img"
                              src={logoPreview}
                              alt="Company Logo"
                              sx={{
                                height: 32,
                                width: 'auto',
                                maxWidth: 80,
                                objectFit: 'contain'
                              }}
                            />
                          )}
                          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                            {profileData.company || 'Company Name'}
                          </Typography>
                        </Box>
                      )}
                      {/* <Typography variant="body2" sx={{
                        bgcolor: colors.frontHighlight,
                        px: badge.padding,
                        py: 0.5,
                        borderRadius: badge.borderRadius,
                        fontSize: '0.75rem',
                        transform: badge.transform || 'none',
                        borderLeft: badge.borderLeft || 'none'
                      }}>
                        EMPLOYEE ID
                      </Typography> */}
                    </Box>
                  )}

                  {/* Photo & Personal Info */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: layout.infoLayout === 'centered' ? 'center' : 'flex-start',
                    textAlign: layout.infoLayout === 'centered' ? 'center' : 'left',
                    my: 1
                  }}>
                    {customOptions.includePhoto && (
                      <Avatar
                        src={profileData.avatar || "/api/placeholder/150/150"}
                        alt={profileData.name || 'Employee'}
                        sx={{
                          width: layout.avatarSize,
                          height: layout.avatarSize,
                          border: `4px solid ${colors.frontAccent}`,
                          boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                          mb: 2,
                          alignSelf: layout.infoLayout === 'centered' ? 'center' : 'flex-start'
                        }}
                      />
                    )}
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {profileData.name || 'Employee Name'}
                    </Typography>

                    {customOptions.includePosition && (
                      <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                        {profileData.title || 'Position'}
                      </Typography>
                    )}

                    {customOptions.includeDepartment && (
                      <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.8 }}>
                        {profileData.department || "Department"}
                      </Typography>
                    )}
                  </Box>

                  {/* ID Details */}
                  {customOptions.includeID && (
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        bgcolor: colors.frontHighlight,
                        p: 2,
                        borderRadius: customOptions.roundedCorners ? 2 : 0
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ opacity: 0.7 }}>Employee ID:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {idCardNumber || "EMP-" + Math.floor(10000 + Math.random() * 90000)}
                          </Typography>
                        </Box>

                        {customOptions.includeIssueDates && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ opacity: 0.7 }}>Issue Date:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {issueDate
                                  ? new Date(issueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                  : (profileData.joinDate
                                      ? new Date(profileData.joinDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                      : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                    )
                                }
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ opacity: 0.7 }}>Valid Until:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {validUntil
                                  ? new Date(validUntil).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                  : (profileData.expiryDate
                                      ? new Date(profileData.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                      : new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                    )
                                }
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Card Footer */}
                  {layout.includeFooter && (
                    <Box sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${colors.frontHighlight}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {/* <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {customOptions.customText || 'ID Card v1.0'}
                      </Typography> */}
                      <Typography variant="caption" className="hide-in-pdf" sx={{ fontWeight: 600 }}>
                        Tap to flip →
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Card Back */}
              <Box
                ref={backCardRef}
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  borderRadius: customOptions.roundedCorners ? '16px' : '0',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  color: colors.backText,
                  padding: 3,
                  boxShadow: customOptions.showShadow ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : 'none',
                  border: customOptions.showBorder ? colors.cardBorder : 'none',
                  // Keep back card positioned exactly on top of front card
                  transform: 'rotateY(180deg)',
                  background: colors.backBg,
                  top: 0,
                  left: 0,
                }}
              >
                {/* Pattern Background Layer */}
                {designPattern !== 'none' && (
                  <Box sx={getPatternBackgroundStyle()} />
                )}

                {/* Logo Background Layer */}
                {useLogoAsBg && logoPreview && (
                  <Box sx={getLogoBackgroundStyle()} />
                )}

                {/* Content - now wrapped in a container with relative position and z-index */}
                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Company Info */}
                  {customOptions.includeCompany && (
                    <Box sx={{
                      textAlign: 'center',
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${colors.backHighlight}`
                    }}>
                      {customOptions.includeLogo && logoPreview && (
                        <Box
                          component="img"
                          src={logoPreview}
                          alt="Company Logo"
                          sx={{
                            height: 40,
                            mb: 1,
                            objectFit: 'contain'
                          }}
                        />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {profileData.company || 'Company Name'}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {profileData.companyAddress || 'Company Address'}
                      </Typography>
                    </Box>
                  )}

                  {/* Contact Information */}
                  {customOptions.includeContactInfo && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: colors.accent
                      }}>
                        CONTACT INFORMATION
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Mail size={16} color={colors.accent} />
                          <Typography variant="body2">{profileData.email || 'email@company.com'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Phone size={16} color={colors.accent} />
                          <Typography variant="body2">{profileData.phone || '+1 (555) 123-4567'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <MapPin size={16} color={colors.accent} />
                          <Typography variant="body2">{profileData.location || 'City, Country'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Linkedin size={16} color={colors.accent} />
                          <Typography variant="body2">
                            linkedin.com/in/{profileData.linkedinUrl?.split('/').pop() || "profile"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* QR Code */}
                  {customOptions.includeQR && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 3,
                      gap: 1
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.accent }}>
                        {customOptions.qrCodeType === 'vcard' ? 'SCAN FOR CONTACT INFO' : 'SCAN FOR PROFILE'}
                      </Typography>

                      <Box
                        ref={qrCodeRef}
                        sx={{
                          p: 1,
                          bgcolor: 'white',
                          borderRadius: 1,
                          display: 'inline-flex'
                        }}
                      >
                        <QRCodeSVG
                          value={getQRData()}
                          size={120}
                          fgColor="#000000"
                          bgColor="#FFFFFF"
                          level="M"
                          includeMargin={true}
                        />
                      </Box>

                      {/* QR Caption */}
                      <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 180 }}>
                        {customOptions.qrCodeType === 'vcard'
                          ? 'Scan to add this contact to your phone'
                          : 'Scan to view the online profile'}
                      </Typography>
                    </Box>
                  )}

                  {/* Skills Section */}
                  {customOptions.includeSkills && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: colors.accent
                      }}>
                        PROFESSIONAL SKILLS
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(profileData.skills || ['Skill 1', 'Skill 2', 'Skill 3']).slice(0, 5).map((skill, index) => (
                          <Box key={index} sx={{
                            bgcolor: colors.backHighlight,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: badge.borderRadius,
                            transform: badge.transform || 'none',
                            borderLeft: badge.borderLeft || 'none',
                            fontSize: '0.75rem'
                          }}>
                            {skill}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Emergency Contact */}
                  {customOptions.includeEmergencyInfo && (
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="subtitle2" sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: '#f87171'
                      }}>
                        IN CASE OF EMERGENCY
                      </Typography>
                      {/* Show emergency contact info if provided */}
                      {emergencyContact.name || emergencyContact.phone ? (
                        <Grid container spacing={1}>
                          {emergencyContact.name && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <User size={14} /> {emergencyContact.name} {emergencyContact.relation ? `(${emergencyContact.relation})` : ''}
                              </Typography>
                            </Grid>
                          )}
                          {emergencyContact.phone && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone size={14} /> {emergencyContact.phone}
                              </Typography>
                            </Grid>
                          )}
                          {emergencyContact.email && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Mail size={14} /> {emergencyContact.email}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        <Typography variant="body2">
                          Please contact HR Department: hr@{profileData.companyUrl?.replace(/(https?:\/\/)?(www\.)?/, '') || "company.com"}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Card Footer */}
                  {layout.includeFooter && (
                    <Box sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${colors.backHighlight}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        This card is company property
                      </Typography>
                      <Typography variant="caption" className="hide-in-pdf" sx={{ fontWeight: 600 }}>
                        ← Tap to flip
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportPDF}
                sx={{
                  bgcolor: colors.primary,
                  '&:hover': {
                    bgcolor: colors.secondary,
                  },
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} /> : 'Export ID Card (PDF)'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<RotateCw />}
                onClick={handleFlip}
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': {
                    borderColor: colors.secondary,
                    color: colors.secondary,
                    bgcolor: 'rgba(0,0,0,0.04)',
                  },
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                }}
              >
                Flip Card
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* Settings Column */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, borderRadius: 2, height: '100%', overflow: 'auto', maxHeight: '800px' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings size={20} />
            ID Card Customization
          </Typography>

          <Tabs
            value={activeSettingsTab}
            onChange={handleSettingsTabChange}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            <Tab label="Design" icon={<Palette size={16} />} iconPosition="start" />
            <Tab label="Content" icon={<FileText size={16} />} iconPosition="start" />
            <Tab label="QR & Logo" icon={<Camera size={16} />} iconPosition="start" />
          </Tabs>

          {/* Design Tab */}
          {activeSettingsTab === 0 && (
            <Box>
              <FormControl fullWidth margin="normal">
                <InputLabel id="color-scheme-label">Color Scheme</InputLabel>
                <Select
                  labelId="color-scheme-label"
                  value={colorScheme}
                  label="Color Scheme"
                  onChange={handleColorSchemeChange}
                >
                  {Object.entries(colorSchemes).map(([key, scheme]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 20,
                          height: 20,
                          bgcolor: scheme.primary,
                          borderRadius: '50%',
                          border: '2px solid white',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                        }} />
                        {scheme.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel id="design-pattern-label">Background Pattern</InputLabel>
                <Select
                  labelId="design-pattern-label"
                  value={designPattern}
                  label="Background Pattern"
                  onChange={handleDesignPatternChange}
                >
                  {Object.entries(designPatterns).map(([key, pattern]) => (
                    <MenuItem key={key} value={key}>{pattern.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel id="card-layout-label">Card Layout</InputLabel>
                <Select
                  labelId="card-layout-label"
                  value={cardLayout}
                  label="Card Layout"
                  onChange={handleCardLayoutChange}
                >
                  {Object.entries(cardLayouts).map(([key, layout]) => (
                    <MenuItem key={key} value={key}>{layout.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel id="badge-style-label">Badge Style</InputLabel>
                <Select
                  labelId="badge-style-label"
                  value={badgeStyle}
                  label="Badge Style"
                  onChange={handleBadgeStyleChange}
                >
                  {Object.entries(badgeStyles).map(([key, style]) => (
                    <MenuItem key={key} value={key}>{style.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Card Appearance
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.showBorder}
                      onChange={handleOptionChange('showBorder')}
                    />
                  }
                  label="Show Border"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.roundedCorners}
                      onChange={handleOptionChange('roundedCorners')}
                    />
                  }
                  label="Rounded Corners"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.showShadow}
                      onChange={handleOptionChange('showShadow')}
                    />
                  }
                  label="Show Shadow"
                />
                {customOptions.includeLogo && logoPreview && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useLogoAsBg}
                        onChange={(e) => setUseLogoAsBg(e.target.checked)}
                      />
                    }
                    label="Use Logo as Background"
                  />
                )}
              </FormGroup>

              <TextField
                fullWidth
                margin="normal"
                label="Custom Footer Text"
                variant="outlined"
                value={customOptions.customText}
                onChange={handleCustomTextChange}
                placeholder="Custom text for card footer"
                helperText="Leave blank for default text"
              />
            </Box>
          )}

          {/* Content Tab */}
          {activeSettingsTab === 1 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Front Side Elements
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includePhoto}
                      onChange={handleOptionChange('includePhoto')}
                    />
                  }
                  label="Include Photo"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includePosition}
                      onChange={handleOptionChange('includePosition')}
                    />
                  }
                  label="Include Position/Title"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeDepartment}
                      onChange={handleOptionChange('includeDepartment')}
                    />
                  }
                  label="Include Department"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeCompany}
                      onChange={handleOptionChange('includeCompany')}
                    />
                  }
                  label="Include Company Name"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeID}
                      onChange={handleOptionChange('includeID')}
                    />
                  }
                  label="Include ID Number"
                />
                {customOptions.includeID && (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="ID Card Number"
                    variant="outlined"
                    value={idCardNumber}
                    onChange={handleIdCardNumberChange}
                    placeholder="Enter ID Card Number"
                    helperText="Leave blank to generate a random number"
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                          #
                        </Box>
                      ),
                    }}
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeIssueDates}
                      onChange={handleOptionChange('includeIssueDates')}
                    />
                  }
                  label="Include Issue Dates"
                />
                {customOptions.includeIssueDates && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <TextField
                      label="Issue Date"
                      type="date"
                      size="small"
                      value={issueDate}
                      onChange={e => setIssueDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Valid Until"
                      type="date"
                      size="small"
                      value={validUntil}
                      onChange={e => setValidUntil(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
              </FormGroup>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Back Side Elements
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeQR}
                      onChange={handleOptionChange('includeQR')}
                    />
                  }
                  label="Include QR Code"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeContactInfo}
                      onChange={handleOptionChange('includeContactInfo')}
                    />
                  }
                  label="Include Contact Information"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeSkills}
                      onChange={handleOptionChange('includeSkills')}
                    />
                  }
                  label="Include Skills"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeEmergencyInfo}
                      onChange={handleOptionChange('includeEmergencyInfo')}
                    />
                  }
                  label="Include Emergency Info"
                />
              </FormGroup>

              {/* Emergency Contact Fields (shown only when emergency info is enabled) */}
              {customOptions.includeEmergencyInfo && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px dashed rgba(0,0,0,0.1)' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AlertCircle size={16} color="#ef4444" />
                    Emergency Contact
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={emergencyContact.name}
                        onChange={handleEmergencyContactChange('name')}
                        placeholder="Emergency contact name"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Relationship"
                        value={emergencyContact.relation}
                        onChange={handleEmergencyContactChange('relation')}
                        placeholder="e.g. Spouse, Parent"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone"
                        value={emergencyContact.phone}
                        onChange={handleEmergencyContactChange('phone')}
                        placeholder="Emergency phone number"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        value={emergencyContact.email}
                        onChange={handleEmergencyContactChange('email')}
                        placeholder="Emergency email address"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* QR Code & Logo Tab */}
          {activeSettingsTab === 2 && (
            <Box>
              {/* Logo Upload Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Company Logo
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customOptions.includeLogo}
                      onChange={handleOptionChange('includeLogo')}
                    />
                  }
                  label="Include Company Logo"
                />

                <Box sx={{
                  mt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: '1px dashed rgba(0,0,0,0.2)'
                }}>
                  {logoPreview ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        component="img"
                        src={logoPreview}
                        alt="Company Logo"
                        sx={{
                          maxWidth: '100%',
                          height: 'auto',
                          maxHeight: 80,
                          mb: 2
                        }}
                      />
                      <Button
                        size="small"
                        startIcon={<Upload size={16} />}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        Change Logo
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      onClick={() => logoInputRef.current?.click()}
                      sx={{ my: 1 }}
                    >
                      Upload Logo
                    </Button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                  <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    Recommended: PNG or SVG with transparent background.<br />
                    Max 2MB file size.
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* QR Code Options */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  QR Code Type
                </Typography>
                <RadioGroup
                  value={customOptions.qrCodeType}
                  onChange={handleQRTypeChange}
                >
                  <FormControlLabel
                    value="vcard"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCard size={16} />
                        <Typography>vCard (Contact Information)</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="link"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Globe size={16} />
                        <Typography>Web Link (Profile URL)</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                QR Code Preview
              </Typography>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: '1px dashed rgba(0,0,0,0.1)'
              }}>
                <Box sx={{
                  p: 1,
                  bgcolor: 'white',
                  borderRadius: 1
                }}>
                  <QRCodeSVG
                    value={getQRData()}
                    size={150}
                    fgColor="#000000"
                    bgColor="#FFFFFF"
                    level="M"
                    includeMargin={true}
                  />
                </Box>
                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                  {customOptions.qrCodeType === 'vcard'
                    ? 'vCard with contact details'
                    : 'Link to online profile'}
                </Typography>
              </Box>
            </Box>
          )}
          {renderStorageSettings()}
        </Paper>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ProfilePageIDCard;